import { MatchUpStatusEnum } from './../../_shared/models/csTypes/enums/matchup_status';
import { OnCourtPlayer, Point, Server } from '../../_shared/models/point.model';
import { Injectable, inject } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { Game, GameFormat } from '../../_shared/models/game.model';
import { IPlayerCourtTimeLog, Match, SubstitutionTracker } from '../../_shared/models/match.model';
import {
  ScoreFormat,
  SetType,
  TimedPointsOrGames,
} from '../../_shared/models/score-format.model';
import { Set } from '../../_shared/models/set.model';
import { ToastService } from '../../_core/services/toast.service';
import { Router } from '@angular/router';
import { CourtSide } from '../../_shared/models/enums/court-side';
import { DisciplineEnum } from '../../_shared/models/csTypes/enums/discipline';
import { PickleballGameFormat } from '../../_shared/models/enums/pickleball-game-format';
import {
  IocSelectorOption,
  IocSelectorService,
} from '../../_shared/components/ioc-selector/ioc-selector.service';
import { Tie } from '../../_shared/models/tie.model';
import { Subject } from 'rxjs';

export interface TieScore {
  matchId: string;
  side1Score: number;
  side2Score: number;
}

@Injectable({
  providedIn: 'root',
})
export class BoardHelpService {
  private iocSelectorService = inject(IocSelectorService);

  point = {
    0: '0',
    1: '15',
    2: '30',
    3: '40',
    4: 'A',
  };

  tie: Tie
  tieMatches: any = {}
  tieScore: any = {};
  matchUps:Match[]; 
  isDoublePoint:boolean = false;
  playerPositions: any
  isBoltEnd: boolean = false;
  private _allowFansToScore: boolean = false;
  isUserFan: boolean = false;
  totalFansCount: number = 0;

  private tieScoreSubject = new Subject<TieScore>();
  public tieScore$ = this.tieScoreSubject.asObservable();

  constructor(
    private alertController: AlertController,
    private toast: ToastService,
    public router: Router
  ) {}

  public get allowFansToScore(): boolean {
    return this._allowFansToScore;
  }
  public set allowFansToScore(value: boolean) {
    this._allowFansToScore = value;
  }

  notifyScoreUpdate(score: TieScore) {
    this.tieScoreSubject.next(score);
  }

  public updateTieMatchScore(
    tieId: string,
    tieScore: { side1Score: number; side2Score: number }
  ): void {
    this.tieScore[tieId] = tieScore;
  }

  public getCurrentTieScore(tieId: string) {
    return this.tieScore?.[tieId] || null
  }

  public updateTieAggregateScore(
    match: Match
  ):void{
    let result:any;
    if (this.matchUps) {
      const excludeCurrentMatch  = this.matchUps.filter(tieMatch=> tieMatch._id != match._id);
      const otherMatchSide1Score = this.getSideScore(excludeCurrentMatch, 1);
      const otherMatchSide2Score = this.getSideScore(excludeCurrentMatch, 2);
      const {side1Score, side2Score} = this.currentMatchAllSetsScore(match);
      if (match?.tie) {    
        result = {side1Score: side1Score+otherMatchSide1Score, side2Score: side2Score+otherMatchSide2Score}
      }
    }else {
      result = match?.tie ? this.tieScore[match?.tie]: {side1Score: 0 , side2Score: 0}
    }    

    this.notifyScoreUpdate({
      matchId: match._id,
      side1Score: result.side1Score,
      side2Score: result.side2Score
    });
  }

  private currentMatchAllSetsScore(match: Match):{ side1Score: number; side2Score: number } {
    let side1Score = 0;
    let side2Score = 0;

    match.sets.forEach((set:any) => {
      side1Score += set?.side1Score;
      side2Score += set?.side2Score;
    });
    return { side1Score, side2Score };
  }
  public resetTieScore(): void {
    /**REST TIE SCORE */
    this.tieScore = {};
  }

  public getSideScore(matches: Match[], sideNumber) {
    let sideScore = 0;
    this.tie?.tieFormat?.collectionDefinitions.forEach((cd) => {
      const collectionMatches = matches.filter((match) => {
        if (match.collectionId === cd.collectionId) {
          return match;
        }
      });

      // If a collection has value then make sure that a team has won more points than assigned by collectionValue (D1 doubles)
      if (cd.collectionValue) {
        let collectionSideScore = 0;
        collectionMatches.forEach((match) => {
          if (match.winningSide === sideNumber) {
            collectionSideScore++;
          }
        });

        if (
          collectionSideScore >=
          Math.floor(collectionMatches.length / 2) + 1
        ) {
          sideScore++;
        }
      } else {
        // If each match in collection is worth X number of points add that number of points to a team
        collectionMatches.forEach((match: Match, j) => {
          if (this.tie?.tieFormat?.winCriteria.aggregateValue) {
            match.score?.sets?.forEach((set) => {
              const value =
                cd.collectionValueProfile[j].gameValue ||
                cd.collectionValueProfile[j].pointValue;
              sideScore += value * set[`side${sideNumber}Score`];
            });
          } else if (match.winningSide === sideNumber) {
            sideScore += cd.collectionValueProfile[j].matchUpValue;
          }
        });
      }
    });

    return sideScore;
  }


  // *********** START QUICK POINT REFERENCE HELPERS ******************

  public getLastSet(match: Match) {
    if (match.sets) {
      return match.sets[match.sets.length - 1];
    }
    return null;
  }

  public getLastSetServingAndReturningSide(match: Match): Server | null {
    if (match?.sets?.length > 1) {
      return (
        match.sets[match.sets.length - 2]?.games?.[0]?.points?.[0]?.server ??
        null
      );
    }
    return null;
  }

  public getLastCompletedSet(match:Match):any {
    if (match?.sets?.length > 1) {
      return (
        match.sets[match.sets.length - 2] ??
        null
      );
    }
    return null;
  }

  public getLastGame(match: Match) {
    const lastSet = this.getLastSet(match);
    return lastSet?.games?.length
      ? lastSet.games[lastSet.games.length - 1]
      : null;
  }

  //       if (lastSet.games.length === 1) {
  //         return previousSet.games[previousSet.games.length - 2];
  //       } else if (lastSet.games.length === 2) {
  //         return previousSet.games[previousSet.games.length - 1];
  //       }
  //     }

  //     return lastSet.games[lastSet.games.length - 3];
  //   }

  public getNextToLastGame(match: Match) {
    let game: Game;
    if (this.getLastSet(match)?.games.length === 1) {
      if (match.sets.length > 1) {
        game =
          match.sets[match.sets.length - 2].games[
            match.sets[match.sets.length - 2].games.length - 1
          ];
      } else {
        game = null;
      }
    } else {
      game =
        this.getLastSet(match)?.games[this.getLastSet(match).games.length - 2];
    }
    return game;
  }

  public getNextToNextToLastGame(match: Match): Game {
    const lastSet = this.getLastSet(match);

    if (match.sets.length > 1 && lastSet.games.length < 3) {
      const previousSet = match.sets[match.sets.length - 2];

      if (lastSet.games.length === 1) {
        return previousSet.games[previousSet.games.length - 2];
      } else if (lastSet.games.length === 2) {
        return previousSet.games[previousSet.games.length - 1];
      }
    }

    return lastSet.games[lastSet.games.length - 3];
  }
  public getLastPoint(match: Match | any) {
    if (match?.sets?.length) {
      if (this.getLastGame(match).points.length) {
        return this.getLastGame(match).points[
          this.getLastGame(match).points.length - 1
        ];
      } else {
        return this.getNextToLastGame(match).points[
          this.getNextToLastGame(match).points.length - 1
        ];
      }
    }

    return null;
  }

  public getNextToLastPoint(match: Match): Point {
    if (this.getLastGame(match)?.points.length > 1) {
      return this.getLastGame(match).points[
        this.getLastGame(match).points.length - 2
      ];
    } else if (this.getNextToLastGame(match)) {
      return this.getNextToLastGame(match).points[
        this.getNextToLastGame(match).points.length - 1
      ];
    }
  }

  /**
   * Retrieves the last three points from the most recent game of the match.
   * If the last game has more than one point, it returns the last three;
   * otherwise, it returns an empty array.
   */
  public getLastThreePointsFromLastGame(match: Match): Point[] {
    const lastGame = this.getLastGame(match);
    return lastGame?.points?.length > 1
      ? lastGame.points.slice(-3)
      : [];
  }

  // *********** END QUICK POINT REFERENCE HELPERS ******************

  public getServer(match) {
    return match.sets.length > 0 && this.getLastGame(match).points.length > 0
      ? this.getLastPoint(match).server
      : null;
  }

  removeLastPoint(match: Match) {
    const scoreFormat = this.getScoreFormat(
      match.matchFormat,
      match.discipline
    );

    // Remove last point
    let lastGame = this.getLastGame(match);
    lastGame?.points?.splice(-1, 1);

    // If this is the first point of the game, delete current game
    if (!lastGame?.points.length) {
      let lastSet = this.getLastSet(match);
      lastSet.games.splice(-1, 1);

      // If this is the first game of the set, delete current set and set previous set as unfinished
      if (!lastSet.games.length) {
        match.sets.splice(-1, 1);
        lastSet = this.getLastSet(match);
        lastSet.winningSide = null;
        lastSet.isCompleted = false;
      }
    }
    // reset last point
    const lastPoint = this.getLastPoint(match);
    lastPoint.side1Score = 0;
    lastPoint.side2Score = 0;
    lastPoint.winningSide = null;
    lastPoint.winReason = null;
    lastPoint.rallyLength = null;
    lastPoint.shots = [];

    // Reset the the game as unfinished
    lastGame = this.getLastGame(match);
    lastGame.winningSide = null;

    if (scoreFormat.timedPointsOrGames === TimedPointsOrGames.POINT) {
      const nextTolastPoint: Point | undefined = this.getNextToLastPoint(match);
      lastGame.side1Score = nextTolastPoint?.side1Score || 0;
      lastGame.side2Score = nextTolastPoint?.side2Score || 0;

      const lastSet = this.getLastSet(match);
      lastSet.side1Score = nextTolastPoint?.side1Score || 0;
      lastSet.side2Score = nextTolastPoint?.side2Score || 0;
    } else {
      lastGame.side1Score = 0;
      lastGame.side2Score = 0;
    }
  }

  getPlayersNames(
    match: Match,
    sideNumber: number,
    type = 'initial',
    playerNumber?: number
  ) {
    if (!sideNumber) return null;
    const players = match.sides[sideNumber - 1].players;
    let playerNames = '';

    if (playerNumber) {
      playerNames = players[playerNumber - 1]?.participant
        ? `${players[playerNumber - 1]?.participant?.last_name?.toUpperCase()}, ${
            players[playerNumber - 1]?.participant?.first_name
          }`
        : `Player ${sideNumber}`;
    } else {
      players.forEach((player, i) => {
        if (i === 1) {
          playerNames += ' / ';
        }

        if (player.participant) {
          const firstName =
            type === 'initial'
            ? player.participant?.first_name?.charAt(0) + '.'
              : player.participant?.first_name;
          playerNames += `${player.participant?.last_name?.toUpperCase()}, ${firstName}`;
        } else {
          playerNames += `Player ${sideNumber}`;
        }
      });
    }

    return playerNames;
  }

  getPointScore(match: Match, sideNumber: number) {
    if (match.sets.length) {
      const lastSet = match.sets[match.sets.length - 1];
      const lastGame = lastSet.games[lastSet.games.length - 1];
      const scoreFormat: ScoreFormat = this.getScoreFormat(
        match.matchFormat,
        match.discipline
      );

      if (scoreFormat.timedPointsOrGames === TimedPointsOrGames.POINT) {
        return this.aggregatePoints(match, sideNumber);
      } else {
        if (match.matchStatus === MatchUpStatusEnum.COMPLETED) {
          return 0;
        } else if (lastGame.points.length < 2) {
          // For badminton and pickleball, show the current score even for the first point
          if (match.discipline === DisciplineEnum.BADMINTON || 
              match.discipline === DisciplineEnum.PICKLEBALL || 
              match.discipline === DisciplineEnum.TABLE_TENNIS || 
              match.discipline === DisciplineEnum.SQUASH) {
            return lastGame.points.length > 0 ? lastGame.points[0][`side${sideNumber}Score`] || 0 : 0;
          }
          return 0;
        } else {
          const lastPoint =
            this.isFinalSet(match) &&
            scoreFormat.lastSetTiebreakTo &&
            match.winningSide
              ? lastGame.points[lastGame.points.length - 1] // TODO for 5 sets
              : lastGame.points[lastGame.points.length - 2];

          return lastGame.gameFormat === GameFormat.TIEBREAK ||
            match.discipline === DisciplineEnum.PICKLEBALL ||
            match.discipline === DisciplineEnum.BADMINTON || 
            match.discipline === DisciplineEnum.TABLE_TENNIS || 
            match.discipline === DisciplineEnum.SQUASH
            ? lastPoint[`side${sideNumber}Score`]
            : this.point[lastPoint[`side${sideNumber}Score`]];
        }
      }
    }
  }

  aggregatePoints(match: Match, sideNumber: number): number {
    let sideScore = 0;
    match.sets.forEach((set) => {
      sideScore += Number(set[`side${sideNumber}Score`]);
    });

    return sideScore;
  }

  getGameScore(set: Set, sideNumber: number) {
    return set.games.length < 2 || set.winningSide || set.isCompleted
      ? set.games[set.games.length - 1][`side${sideNumber}Score`]
      : set.games[set.games.length - 2][`side${sideNumber}Score`];
  }

  // ************* TRACKER COURT ***************
  addNewShot(
    match: Match,
    sideNumber,
    shotType,
    shotOutcome,
    shotMadeFrom?,
    shotDetail?,
    bounceAt?
  ) {
    let playerNumber: number = null;
    if (match.matchType === 'S') {
      playerNumber = 1;
    } else if (match.matchType === 'D' && shotType === 'SE') {
      playerNumber = this.getLastPoint(match).server.playerNumber;
    }

    // TODO Who is returning?

    return {
      participant: this.getParticipantId(match, sideNumber, playerNumber),
      shotNumber: this.getLastPoint(match).shots.length + 1,
      shotOutcome,
      shotType,
      shotMadeFrom,
      shotDetail,
      side: this.getLastPoint(match).server.sideNumber,
    };
  }

  getServerSideNumber(match) {
    return this.getLastPoint(match).server.sideNumber;
  }

  getScoreFormat(matchFormat: string, discipline: DisciplineEnum, score?: any, requestFromMatchFormatForm?: boolean): ScoreFormat {
    if (discipline === DisciplineEnum.PICKLEBALL) {
      return this.getPickleballScoreFormat(matchFormat);
    } else if (discipline === DisciplineEnum.BADMINTON) {
      return this.getBadmintonScoreFormat(matchFormat);
    } else if (discipline === DisciplineEnum.TABLE_TENNIS) {
      return this.getTableTennisScoreFormat(matchFormat);
    } else if (discipline === DisciplineEnum.SQUASH) {
      return this.getSquashScoreFormat(matchFormat);
    } else {
      return this.getTennisScoreFormat(matchFormat, score, requestFromMatchFormatForm);
    }
  }

  getPickleballScoreFormat(matchFormat: string): ScoreFormat {
    if (!matchFormat) return null;

    let scoreFormat: ScoreFormat;
    const split = matchFormat.split('-');
    const numberOfSets = Number(split[0].split('SET')[1]);
    const numberOfGamesInSet = 1;

    let ads: boolean;
    if (split[1].includes('NOAD')) {
      ads = !split[1].includes('NOAD');
    }

    let timedSetDuration: number;
    let timedPointsOrGames: TimedPointsOrGames;
    if (split[1].includes('P')) {
      // S:10P Remove S: and P to get number of minutes
      timedSetDuration = Number(split[1].substring(2).slice(0, -1));
      timedPointsOrGames = TimedPointsOrGames.POINT;
    }

    const winBy2 = split[1].includes('WB2');

    const gameSplit = split[2].split('/');

    const numberOfPointsInGame = Number(gameSplit[0].split(':')[1]);

    let setType: SetType;

    if (gameSplit[1] === 'R') setType = SetType.RALLY;
    else if (gameSplit[1] === 'S') setType = SetType.STANDARD;
    else if (gameSplit[1] === 'M') setType = SetType.MLP;

    let numberOfSetsToWin: number;
    if (numberOfSets === 1) numberOfSetsToWin = 1;
    else if (numberOfSets === 3) numberOfSetsToWin = 2;
    else if (numberOfSets === 5) numberOfSetsToWin = 3;

    let lastSetGameType: SetType;
    let lastSetNumberOfPoints: number;
    let lastSetGameFormat: PickleballGameFormat;

    if (split[3]) {
      const lastSetConfig = split[3].split(':')[1].split('/');

      lastSetGameType =
        lastSetConfig[1] === 'S' ? SetType.STANDARD : SetType.RALLY;
      lastSetNumberOfPoints = Number(lastSetConfig[0]);
      lastSetGameFormat =
        lastSetConfig[2] === 'WB2'
          ? PickleballGameFormat.WIN_BY_2
          : PickleballGameFormat.DECIDING_POINT;
    }

    scoreFormat = {
      numberOfSets,
      numberOfSetsToWin,
      numberOfGamesInSet,
      numberOfPointsInGame,
      timedSetDuration,
      timedPointsOrGames,
      setType,
      ads,
      winBy2,
      lastSetGameType,
      lastSetNumberOfPoints,
      lastSetGameFormat,
    };

    return scoreFormat;
  }

  getBadmintonScoreFormat(matchFormat: string): ScoreFormat {

    if (!matchFormat) return new ScoreFormat();

    const scoreFormat = new ScoreFormat();

    // Number of sets
    const setsMatch = matchFormat.match(/SET(\d+)/i);
    const numberOfSets = setsMatch ? Number(setsMatch[1]) : 3;
    scoreFormat.numberOfSets = numberOfSets;
    if (numberOfSets === 1) scoreFormat.numberOfSetsToWin = 1;
    else if (numberOfSets === 3) scoreFormat.numberOfSetsToWin = 2;
    else if (numberOfSets === 5) scoreFormat.numberOfSetsToWin = 3;
    else scoreFormat.numberOfSetsToWin = Math.ceil(numberOfSets / 2);

    // Always one game per set for badminton
    scoreFormat.numberOfGamesInSet = 1;

    // Badminton always uses rally scoring and win-by-2
    scoreFormat.setType = SetType.RALLY;
    scoreFormat.winBy2 = true;

    // Timed matches detection (e.g., S:10P)
    const timedMatch = matchFormat.match(/S:\d+P|:\d+P/i) || matchFormat.match(/P\d+|P/i);
    if (timedMatch && /P/i.test(timedMatch[0])) {
      // try to extract minutes like S:10P or 10P
      const m = timedMatch[0].match(/(\d+)/);
      if (m) {
        scoreFormat.timedSetDuration = Number(m[1]);
        scoreFormat.timedPointsOrGames = TimedPointsOrGames.POINT;
      }
    }

    // Extract TB points and cap from the whole string (works for all examples)
    const tbMatch = matchFormat.match(/TB(\d+)/i);
    const capMatch = matchFormat.match(/@(\d+)/);

    let numberOfPointsInGame: number;

    if (tbMatch) {
      numberOfPointsInGame = Number(tbMatch[1]);
    } else {
      // default to 21 if not specified
      numberOfPointsInGame = 21;
    }

    scoreFormat.numberOfPointsInGame = numberOfPointsInGame;
    
    // Only set capOfPoints if explicitly provided in the match format
    if (capMatch) {
      scoreFormat.capOfPoints = Number(capMatch[1]);
    }

    return scoreFormat;
  }


  private getTennisScoreFormat(matchFormat: string, score?: any, requestFromMatchFormatForm?: boolean): ScoreFormat {
    const scoreFormat = new ScoreFormat();
    const split = matchFormat.split('-');
    const currentSetNumber = score?.sets?.length;

    // *** PART 1 - MATCH CONFIGURATION ***

    // Number of sets needed to win a match
    const numberOfSets = Number(split[0].split('SET')[1]);
    scoreFormat.numberOfSets = numberOfSets;
    if (numberOfSets === 1) scoreFormat.numberOfSetsToWin = 1;
    else if (numberOfSets === 3) scoreFormat.numberOfSetsToWin = 2;
    else if (numberOfSets === 5) scoreFormat.numberOfSetsToWin = 3;

    // *** PART 2 - SET CONFIGURATION ***

    // Number of games in the set
    const setConfig = split[1].split('/');

    let numberOfGames: string = setConfig[0].split(':')[1];

    // Ads
    scoreFormat.ads = numberOfGames.includes('NOAD') ? false : true;
    scoreFormat.setType = SetType.STANDARD;

    if (
      numberOfGames.includes('G') ||
      (!numberOfGames.includes('TB') && numberOfGames.includes('P'))
    ) {
      scoreFormat.setType = SetType.TIMED;
      scoreFormat.timedPointsOrGames = numberOfGames.includes('G')
        ? TimedPointsOrGames.GAME
        : TimedPointsOrGames.POINT;
      scoreFormat.timedSetDuration = Number(numberOfGames.slice(0, -1));

      return scoreFormat;
    } else if (numberOfGames.length > 2) {
      // Sets
      if (numberOfGames.includes('TB')) {
        // If the format is SET3-S:TB10 we need to play best of 3 10PT tiebreak sets
        scoreFormat.setType = SetType.TIEBREAK;
        scoreFormat.tiebreakTo = numberOfGames.includes('TB10') ? 10 : 7;
      } else {
        // Get number of games by removing NOAD from the string
        numberOfGames = numberOfGames.slice(0, -4);
      }
    }
    scoreFormat.numberOfGamesInSet = Number(numberOfGames);

    if (scoreFormat.setType === SetType.STANDARD) {
      // Play regular set tiebreak to
      const setTiebreakConfig = setConfig[1].split('@');
      scoreFormat.tiebreakType = setTiebreakConfig[0];

      scoreFormat.tiebreakTo = Number(setTiebreakConfig[0].split('TB')[1]);

      //  Tiebreak at different stage of the set
      scoreFormat.tiebreakAt = setTiebreakConfig[1]
        ? Number(setTiebreakConfig[1])
        : Number(scoreFormat.numberOfGamesInSet);

      // Game starting point / Hardcoded for thirty30
      scoreFormat.gameStartingPoint = setConfig[2] ? 2 : 0;

      // *** PART 3 - LAST SET CONFIGURATION ***

      // Last set format
      scoreFormat.lastSet = split[2] ? split[2].split(':')[1] : '';
      const hasTB10at6 = matchFormat.includes("F:6/TB10@6");
      if (hasTB10at6) {
        scoreFormat.lastSet = '';
        // this is special case for TB10@6, 
        // we need to set the lastSet to TB10@6 if it is requested from the match format form
        if (requestFromMatchFormatForm && !score) {
          scoreFormat.lastSet = 'TB10@6';
        }
      }

      if (
        scoreFormat.lastSet === '4' ||
        scoreFormat.lastSet === '6' ||
        scoreFormat.lastSet === '8'
      ) {
        scoreFormat.lastSet = 'WB2';
      }

      // this  for  Last set 10pt tiebreak at 6-6
      // this should apply for last set tiebreak only after 6-6 and play till 10 points
      if (currentSetNumber === numberOfSets) {
        if (hasTB10at6) {
          const lastSet = score?.sets[score?.sets?.length - 1];
          if (lastSet.side1Score >= 6 && lastSet.side2Score >= 6) {
            scoreFormat.lastSet = 'TB10@6';
            scoreFormat.lastSetTiebreakTo = 10;
            scoreFormat.tiebreakAt = 6;
            scoreFormat.tiebreakType = 'TB10';
            scoreFormat.tiebreakTo = 10;
          }else {
            scoreFormat.lastSet = '';
          }
        }
      }

      if (scoreFormat.lastSet && scoreFormat.lastSet !== 'WB2' && scoreFormat.lastSet !== 'TB10@6') {
        scoreFormat.lastSetTiebreakTo = Number(split[2].split('TB')[1]);
      }

      
    } else {
      scoreFormat.tiebreakType = split[1].split(':')[1];
    }
        
    return scoreFormat;
  }

  getTableTennisScoreFormat(matchFormat: string): ScoreFormat {
    if (!matchFormat) return new ScoreFormat();

    const scoreFormat = new ScoreFormat();

    // Table Tennis Rules:
    // - Games are played to 11 points (or other specified number)
    // - Must win by 2 points (no cap - games continue until 2-point lead)
    // - Always uses rally scoring (points can be scored by either player)
    // - Service alternates every 2 points until 10-10, then every point
    // - Matches are typically best of 5 (need 3 sets) or best of 7 (need 4 sets)
    // - Each set consists of one game (not multiple games)

    // Number of sets (e.g., SET5 -> best of 5 sets, SET3 -> best of 3 sets)
    const setsMatch = matchFormat.match(/SET(\d+)/i);
    const numberOfSets = setsMatch ? Number(setsMatch[1]) : 5;
    scoreFormat.numberOfSets = numberOfSets;
    
    // Calculate number of sets needed to win (best-of format)
    // Best of 1: need 1 set, Best of 3: need 2 sets, Best of 5: need 3 sets, Best of 7: need 4 sets
    if (numberOfSets === 1) {
      scoreFormat.numberOfSetsToWin = 1;
    } else if (numberOfSets === 3) {
      scoreFormat.numberOfSetsToWin = 2;
    } else if (numberOfSets === 5) {
      scoreFormat.numberOfSetsToWin = 3;
    } else if (numberOfSets === 7) {
      scoreFormat.numberOfSetsToWin = 4;
    } else {
      // For any other number of sets, calculate as (numberOfSets + 1) / 2 rounded up
      scoreFormat.numberOfSetsToWin = Math.ceil(numberOfSets / 2);
    }

    // Table tennis: Always one game per set (each set is a single game)
    scoreFormat.numberOfGamesInSet = 1;

    // Table tennis: Always uses rally scoring (points can be scored on any serve)
    scoreFormat.setType = SetType.RALLY;
    
    // Table tennis: Must win by 2 points (no cap - games continue until 2-point lead is achieved)
    // At 10-10 (deuce), play continues until one player leads by 2 points
    scoreFormat.winBy2 = true;

    // Extract number of points in game from TB notation (e.g., TB11 -> 11 points, TB21 -> 21 points)
    // Default to 11 points if not specified (standard table tennis game length)
    const tbMatch = matchFormat.match(/TB(\d+)/i);
    const numberOfPointsInGame = tbMatch ? Number(tbMatch[1]) : 11;
    scoreFormat.numberOfPointsInGame = numberOfPointsInGame;

    // Timed matches detection (e.g., S:10P for 10-minute timed match)
    // Table tennis can have timed matches where the player with most points at time limit wins
    const timedMatch = matchFormat.match(/S:\d+P|:\d+P/i) || matchFormat.match(/P\d+|P/i);
    if (timedMatch && /P/i.test(timedMatch[0])) {
      const m = timedMatch[0].match(/(\d+)/);
      if (m) {
        scoreFormat.timedSetDuration = Number(m[1]);
        scoreFormat.timedPointsOrGames = TimedPointsOrGames.POINT;
      }
    }

    return scoreFormat;
  }

  getSquashScoreFormat(matchFormat: string): ScoreFormat {
    if (!matchFormat) return new ScoreFormat();

    const scoreFormat = new ScoreFormat();

    // Squash Rules:
    // Rally scoring:
    // - Games to 11, 15, or 21 points (default: 11)
    // - Must win by 2 points (no cap)
    // - Point awarded on every rally
    // - Winner of each rally scores a point and serves next
    //
    // English (HiHo):
    // - Games to 9 or 15 points (default: 9)
    // - At 8-8 (game to 9): receiver chooses set to 9 (sudden death) or set to 10 (win by 2)
    // - At 14-14 (game to 15): receiver chooses set to 15 (sudden death) or set to 17 (win by 2)

    // Number of sets (e.g., SET3 -> best of 3 sets, SET5 -> best of 5 sets)
    const setsMatch = matchFormat.match(/SET(\d+)/i);
    const numberOfSets = setsMatch ? Number(setsMatch[1]) : 3;
    scoreFormat.numberOfSets = numberOfSets;
    
    // Calculate number of sets needed to win (best-of format)
    if (numberOfSets === 1) {
      scoreFormat.numberOfSetsToWin = 1;
    } else if (numberOfSets === 3) {
      scoreFormat.numberOfSetsToWin = 2;
    } else if (numberOfSets === 5) {
      scoreFormat.numberOfSetsToWin = 3;
    } else {
      scoreFormat.numberOfSetsToWin = Math.ceil(numberOfSets / 2);
    }

    // Squash: Always one game per set
    scoreFormat.numberOfGamesInSet = 1;

    // Determine game type from match format
    const isEnglishHiHo = matchFormat.includes('ENGLISH') || matchFormat.includes('HIHO');
    
    // Squash always uses rally scoring
    scoreFormat.setType = SetType.RALLY;
    
    // Squash: Must win by 2 points
    scoreFormat.winBy2 = true;

    // Extract number of points in game from TB notation (e.g., TB11 -> 11 points)
    const tbMatch = matchFormat.match(/TB(\d+)/i);
    let numberOfPointsInGame: number;
    
    if (tbMatch) {
      numberOfPointsInGame = Number(tbMatch[1]);
    } else {
      // Default based on game type
      numberOfPointsInGame = isEnglishHiHo ? 9 : 11;
    }
    
    scoreFormat.numberOfPointsInGame = numberOfPointsInGame;

    return scoreFormat;
  }

  private getParticipantId(match: Match, sideNumber, playerNumber) {
    return playerNumber
      ? match.sides[sideNumber - 1].players[playerNumber - 1].participant?._id
      : null;
  }

  getReturningSide(
    pointNumber: number,
    match?: Match,
    scoreFormat?: ScoreFormat,
    isNewSet?:boolean
  ): CourtSide {
    if (scoreFormat?.setType) {
      if (scoreFormat.setType == SetType.TIMED) {
        // For TIMED matches, the returning side should be determined based on the total score 
        // of both sides within the current match, rather than the cumulative score from all matches 
        // in the team match (tie).
        

        // If the match is part of a tie, recalculate the overall tie score before computing the returning side.
        if (match?.tie) {          
          this.updateTieAggregateScore(match);
        }
        const totalScores:any = match?.sets.reduce(
          (acc, set) => {
            acc.side1Score += set.side1Score || 0;
            acc.side2Score += set.side2Score || 0;
            return acc;
          },
          { side1Score: 0, side2Score: 0 }
        );
        return (totalScores.side1Score + totalScores.side2Score) % 2 === 0
          ? CourtSide.DEUCE
          : CourtSide.AD;
      } else if (scoreFormat.setType === SetType.MLP) {
        return pointNumber % 2 !== 0 ? CourtSide.DEUCE : CourtSide.AD;
      } 
      // if neither TIMED nor MLP, return AD or DEUCE based on point number
      return pointNumber % 2 === 0 ? CourtSide.AD : CourtSide.DEUCE;
    } 
    // keeping this logic for fallback 
    return pointNumber % 2 === 0 ? CourtSide.AD : CourtSide.DEUCE;
  }

  async chooseReturningSide(): Promise<CourtSide> {
    const title = 'Choose returning side';
    const showCancelButton = false;
    const iocSelectorOptions: IocSelectorOption[] = [
      {
        label: 'Deuce',
        value: CourtSide.DEUCE,
      },
      {
        label: 'Ad',
        value: CourtSide.AD,
      },
    ];

    return await this.iocSelectorService.openPopover(
      title,
      iocSelectorOptions,
      showCancelButton
    );
  }

  async selectPlayer(
    match: Match,
    showCancelButton = false,
    title = 'Select server'
  ): Promise<{ sideNumber: number; playerNumber: number; player?: string }> {
    const iocSelectorOptions: IocSelectorOption[] = [];

    for (let i = 0; i < match.sides.length; i++) {
      const side = match.sides[i];
      if (match.matchType === 'D' && side.players.length < 2) {
        const alertHeader = 'Cannot start match';
        const message = 'Add players to enable this operation';
        await this.confirm(alertHeader, message);
        return;
      }

      side.players.forEach((player, j) => {
        iocSelectorOptions.push({
          label: this.getPlayersNames(match, side.sideNumber, 'full', j + 1),
          value: {
            sideNumber: i + 1,
            playerNumber: j + 1,
            player: player.participant?._id || null,
          },
        });
      });
    }

    const data = await this.iocSelectorService.openPopover(
      title,
      iocSelectorOptions,
      showCancelButton
    );

    return data;
  }

  async selectSide(
    match: Match,
    showCancelButton = false,
    title = 'Select server'
  ): Promise<number> {
    const iocSelectorOptions: IocSelectorOption[] = [];
    match.sides.forEach((side, i) => {
      const participantName = this.getPlayersNames(
        match,
        side.sideNumber,
        'full'
      );

      iocSelectorOptions.push({
        label: participantName,
        value: i + 1,
      });
    });

    const data = await this.iocSelectorService.openPopover(
      title,
      iocSelectorOptions,
      showCancelButton
    );

    return data;
  }

  isFinalSet(match: Match) {
    const scoreFormat = this.getScoreFormat(
      match.matchFormat,
      match.discipline
    );
    return (
      scoreFormat.lastSet && match.sets.length === scoreFormat.numberOfSets
    );
  }

  async confirm(header: string, message: string) {
    const alert = await this.alertController.create({
      header: header,
      message: message,
      buttons: [
        {
          text: 'Ok',
        },
      ],
    });

    await alert.present();
  }

  getAreSidesReversed(match: Match): boolean {
    const isChangeOver = this.getIsChangeOver(match);
    const nextToLastPoint = this.getNextToLastPoint(match);
    let areSidesReversed =
      nextToLastPoint.extensions.find((ext) => (ext.name = 'areSidesReversed'))
        ?.value || false;

    if (isChangeOver) {
      areSidesReversed = !areSidesReversed;
      const isOfficial = this.router.url.includes('official-view');
      if (isOfficial)
        this.toast.presentToast(
          'Players need to change sides',
          'dark',
          5000,
          'bottom'
        );
    }

    return areSidesReversed;
  }

  /**
   * Checks if the current set is the final set
   * Used for table tennis to determine when to swap sides at 5 points
   */
  isLastSet(match: Match, scoreFormat: ScoreFormat): boolean {
    return match.sets.length === scoreFormat.numberOfSets;
  }

  getIsChangeOver(match: Match): boolean {
    const lastGame = this.getLastGame(match);
    const lastSet = this.getLastSet(match);
    const nextToLastPoint = this.getNextToLastPoint(match);
    const side1Score = nextToLastPoint.side1Score ?? 0;
    const side2Score = nextToLastPoint.side2Score ?? 0;
    // Check if swap is not already done on the nextToLastPoint
    const isAlreadySwapped = nextToLastPoint.extensions?.find((ext) => ext.name === 'areSidesReversed')?.value;
    const scoreFormat = this.getScoreFormat(match.matchFormat, match.discipline);

    if (lastSet) {
      if (
        lastSet.games.length === 1 &&
        lastGame.points.length === 1 &&
        match.sets.length > 1
      ) {
        if (match.sets[match.sets.length - 2].games.length % 2 === 1) {
          return true;
        }
      } else if (lastGame.gameFormat === GameFormat.STANDARD) {
        const numberOfGamesPlayedInLastSet =
          match.sets[match.sets.length - 1].games.length;

        if (
          numberOfGamesPlayedInLastSet % 2 === 0 &&
          lastGame.points.length === 1
        ) {
          return true;
        }
      } else if (lastGame.gameFormat === GameFormat.TIEBREAK) {
        if (
          lastGame.points.length === 7 ||
          (lastGame.points.length > 6 && (lastGame.points.length - 1) % 6 === 0)
        ) {
          return true;
        }
      } else if(match.discipline === DisciplineEnum.TABLE_TENNIS && this.isLastSet(match, scoreFormat)){
        const swapThreshold = 5;
        
        if (nextToLastPoint) {
          // Swap sides when either team reaches 5 points
          // If score reaches 5 and swap hasn't been done yet, set areSidesReversed to true
          if (!isAlreadySwapped && (side1Score === swapThreshold || side2Score === swapThreshold)) {
            return true;
          }
        }
      }  else if(match.discipline === DisciplineEnum.BADMINTON && this.isLastSet(match, scoreFormat)) {
        const numberOfPointsInGame = scoreFormat.numberOfPointsInGame || 21;
        const swapThreshold = Math.ceil(numberOfPointsInGame / 2);
        // Swap sides when either team reaches the swap threshold

        if (!isAlreadySwapped && (side1Score === swapThreshold || side2Score === swapThreshold)) {
          return true;
        }
      }
    }

    return false;
  }

  async selectServerPlayer(
    match: Match,
    playerID: number
  ): Promise<{ sideNumber: number; playerNumber: number; player?: string }> {
    const iocSelectorOptions: any = [];

    for (let i = 0; i < match?.sides.length; i++) {
      const side = match.sides[i];
      if (match?.matchType === 'D' && side?.players?.length < 2) {
        const alertHeader = 'Cannot start match';
        const message = 'Add players to enable this operation';
        await this.confirm(alertHeader, message);
        return;
      }

      side.players.forEach((player, j) => {
        iocSelectorOptions.push({
          sideNumber: i + 1,
          playerNumber: j + 1,
          player: player.participant?._id || null,
        });
      });
    }

    return iocSelectorOptions.find((option) => option.player === playerID);
  }

  doublePointStats(status: boolean): void {
    this.isDoublePoint = status;
  }

  getDoublePointStats(status: boolean): boolean {
    return this.isDoublePoint;
  }

  updateBoltEndStatus(status: boolean): void {
    this.isBoltEnd = status;
  }

  getBoltEndStatus(): boolean {
    return this.isBoltEnd;
  }


  getPlayerCourtTimeLog(match: Match, sideNumber: number) {
    const playerCourtTimeLog = match.playerCourtTimeLog?.[`side${sideNumber}`] || [];
    return playerCourtTimeLog;
  }
  
  revertSubstitutionsAfterPoint(
    courtTimeLog: { side1: IPlayerCourtTimeLog[] | undefined; side2: IPlayerCourtTimeLog[] | undefined },
    substituteTrack: { side1: SubstitutionTracker[]; side2: SubstitutionTracker[] },
    substitutionEntry: IPlayerCourtTimeLog,
    match?: Match,
    lastPoint?: any
  ): {
    courtTimeLog: { side1: IPlayerCourtTimeLog[]; side2: IPlayerCourtTimeLog[] };
    substituteTrack: { side1: SubstitutionTracker[]; side2: SubstitutionTracker[] };
  } {
    if (
      !substitutionEntry ||
      !substitutionEntry.player || // player is null or undefined
      (typeof substitutionEntry.player === 'object' && !substitutionEntry.player._id)
    ) {
      return { courtTimeLog, substituteTrack };
    }

    const substitutedPlayerId = substitutionEntry.substitutedBy?.toString();
    const sideKey = substitutionEntry.sideNumber === 1 ? 'side1' : 'side2';

    const originalLogs = courtTimeLog[sideKey] || [];
    const updatedCourtLogs: IPlayerCourtTimeLog[] = [];

    let hasRemovedLatestActiveLog = false;
    let originalPlayerId: string | null = null;

    for (const logEntry of originalLogs) {

         const isSubstitutedPlayer =
        logEntry.player &&
        ((typeof logEntry.player === 'object' &&
        logEntry.player._id?.toString() === substitutedPlayerId) || logEntry?.player === substitutedPlayerId);

      const isLatestActiveSub = isSubstitutedPlayer && !logEntry.outTime;

      if (!hasRemovedLatestActiveLog && isLatestActiveSub) {
        // Remove this log entry (do not push to updatedCourtLogs)
        hasRemovedLatestActiveLog = true;
        continue;
      }

      // Clear substitution metadata from the player being replaced
      const isMatchSubstitution =
        logEntry.substitutedBy?.toString() === substitutionEntry.substitutedBy?.toString() &&
        logEntry.outTime &&
        new Date(logEntry.outTime).getTime() === new Date(substitutionEntry.substitutedAt || substitutionEntry.outTime || '').getTime();

      if (isMatchSubstitution) {
        logEntry.substitutedBy = null;
        logEntry.substitutedAt = null;
        logEntry.outTime = null;
        logEntry.outSetNumber = null;
        delete logEntry.pointNumber;        
        // Store the original player ID for server update
        originalPlayerId = typeof logEntry.player === 'object' ? logEntry.player._id : logEntry.player;
      }

      updatedCourtLogs.push(logEntry);
    }

    // Update the logs for the current side
    courtTimeLog[sideKey] = updatedCourtLogs;

    // Decrement substitution counters for the set and match
    const setNumber = substitutionEntry.setNumber || 0;
    const tracker = substituteTrack[sideKey].find(track => track.setNumber === setNumber);

    if (tracker) {
      tracker.perSet = tracker.perSet;
      tracker.perMatch = tracker.perMatch;
      tracker.usedPerSet -= 1;
      tracker.totalUsedInMatch -= 1;
    }

    // Update server player if the removed player was the current server
    if (match && lastPoint?.server && originalPlayerId) {
      const serverSideNumber = lastPoint.server.sideNumber;
      const serverPlayerNumber = lastPoint.server.playerNumber;
      
      // Check if the current server is the player that was substituted in
      if (lastPoint.server.player === substitutedPlayerId && 
          serverSideNumber === substitutionEntry.sideNumber &&
          serverPlayerNumber === substitutionEntry.playerNumber) {
        // Update the server player to the original player
        lastPoint.server.player = originalPlayerId;
      }
    }

    return {
      courtTimeLog,
      substituteTrack
    };
  }

  /**

   * When match starts, we need to get player from sides and set inPoint to 1 and inTime to match start date

   * We should track from playerCourtTimeLog and get the players who are on court at that time

   * @param match 

   * @returns 

   */

  getOnCourtPlayers(match: Match): { side1: OnCourtPlayer[]; side2: OnCourtPlayer[] }|null {
    const toId = (v: any): string => {
      if (!v) return "";
      const s =
        v?._id?.toString?.() ??
        v?.toString?.() ??
        (typeof v === "string" ? v : "");
      return typeof s === "string" ? s.trim() : "";
    };
  
    const makeEntry = (log: any): OnCourtPlayer => ({
      player: toId(log?.player),
      inPoint: log?.inPointNumber ?? 1,
      outPoint: log?.pointNumber ?? null,
      inTime: log?.inTime ?? match.startDate ?? new Date(),
      outTime: log?.outTime ?? null,
      inSetNumber: log?.setNumber ?? 1,
      outSetNumber: log?.outSetNumber ?? null,
    });
  
    const extractActivePlayers = (logs?: any[]) =>
      (logs ?? []).filter(l => !l?.outTime).map(makeEntry);
  
    const side1 = extractActivePlayers(match.playerCourtTimeLog?.side1);
    const side2 = extractActivePlayers(match.playerCourtTimeLog?.side2);
  
    const anyPlayerMissing = [...side1, ...side2].some(e => !e.player || e.player === "");
    return anyPlayerMissing ? null : { side1, side2 };
  }


  /**
   * When before match starts, we need to get player from sides and set inPoint to 1 and inTime to match start date
   * @param match 
   * @returns 
   */

  getOnCourtPlayersFromSides(match: Match): { side1: OnCourtPlayer[]; side2: OnCourtPlayer[] }| null {
    const toIdString = (v: any) =>
      v?._id?.toString?.() ?? v?.toString?.() ?? String(v ?? '');
  
    const makeEntry = (p: any): OnCourtPlayer => ({
      player: toIdString(p.participant),
      inPoint: 1,
      outPoint: null,
      inTime: match.startDate ?? new Date(),
      outTime: null,
      inSetNumber: 1, 
      outSetNumber: null,
    }); 

    const side1 = match.sides?.[0]?.players?.map(makeEntry) ?? [];
    const side2 = match.sides?.[1]?.players?.map(makeEntry) ?? [];

    const anyPlayerMissing =
      side1.some(e => !e.player) || side2.some(e => !e.player);

    return anyPlayerMissing ? null : { side1, side2 };
  }
}
